import { useEffect, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';
import NProgress from 'nprogress';
import 'nprogress/nprogress';

import DashboardLayout from './layouts/DashboardLayout';
import Login from './pages/Login/Login';
import SubCategoryList from './pages/Category/SubCategory/List/SubCategoryList';
import SubCategoryCreate from './pages/Category/SubCategory/Create/SubCategoryCreate';

const Main = lazy(() => import('./pages/Main/page'));
const Test = lazy(() => import('./pages/Test/Test'));
const Chunk = lazy(() => import('./pages/Chunk/Index'));

// Product
const ProductList = lazy(() => import('./pages/Product/List/ProductList'));
const ProductCreate = lazy(
  () => import('./pages/Product/Create/ProductCreate')
);
const ProductUpdate = lazy(
  () => import('./pages/Product/update/ProductUpdate')
);
const ProductPhotoCreate = lazy(
  () => import('./pages/Product/ProductPhoto/Create/ProductPhotoCreate')
);
const ProductItemList = lazy(
  () => import('./pages/Product/item/List/ProductItemList')
);
const ProductItemCreate = lazy(
  () => import('./pages/Product/item/Create/ProductItemCreate')
);
const ProductItemUpdate = lazy(
  () => import('./pages/Product/item/Update/ProductItemUpdate')
);
const ProductLabel = lazy(() => import('./pages/Product/Label/ProductLabel'));

// Category
const CategoryList = lazy(() => import('./pages/Category/List/CategoryList'));
const CategoryCreate = lazy(
  () => import('./pages/Category/Create/CategoryCreate')
);
const CategoryUpdate = lazy(
  () => import('./pages/Category/update/CategoryUpdate')
);
const CategoryPhotoCreate = lazy(
  () => import('./pages/Category/CategoryPhoto/Create/CategoryPhotoCreate')
);

// Company
const CompanyList = lazy(() => import('./pages/Company/List/CompanyList'));
const CompanyCreate = lazy(
  () => import('./pages/Company/Create/CompanyCreate')
);
const CompanyUpdate = lazy(
  () => import('./pages/Company/Update/CompanyUpdate')
);
const CompanyPhotoList = lazy(
  () => import('./pages/Company/CompanyPhoto/List/CompanyPhotoList')
);
const CompanyPhotoCreate = lazy(
  () => import('./pages/Company/CompanyPhoto/Create/CompanyPhotoCreate')
);

// Banner
const BannerList = lazy(() => import('./pages/Banner/List/BannerList'));
const BannerCreate = lazy(() => import('./pages/Banner/Create/BannerCreate'));
const BannerUpdate = lazy(() => import('./pages/Banner/update/BannerUpdate'));

// User & Cart
const UserList = lazy(() => import('./pages/User/List/UserList'));
const UserCreate = lazy(() => import('./pages/User/Create/UserCreate'));
const UserUpdate = lazy(() => import('./pages/User/Update/UserUpdate'));
const CartList = lazy(() => import('./pages/User/cart/list/CartList'));
const CartItem = lazy(() => import('./pages/User/cart/item/CartItem'));

// Task & Archive
const TaskList = lazy(() => import('./pages/Task/List/TaskList'));
const TaskCreate = lazy(() => import('./pages/Task/Create/TaskCreate'));
const TaskUpdate = lazy(() => import('./pages/Task/Update/TaskUpdate'));
const TaskView = lazy(() => import('./pages/Task/View/TaskView'));
const TaskArchive = lazy(() => import('./pages/Archive/TaskArchive'));

// Role
const RoleList = lazy(() => import('./pages/Role/List/RoleList'));
const RoleCreate = lazy(() => import('./pages/Role/RoleCreate'));
const RoleUpdate = lazy(() => import('./pages/Role/RoleUpdate'));
const RolePermission = lazy(
  () => import('./pages/Role/Permission/RolePermission')
);

const SalaryCalculation = lazy(
  () => import('./pages/Salary/SalaryCalculation')
);

const useAuth = () => {
  const cookies = new Cookies(null, { path: '/' });
  const token = cookies.get('token');
  return !!token;
};

const PrivateRoute = () => {
  const auth = useAuth();
  return auth ? <DashboardLayout /> : <Navigate to="/login" replace />;
};

const RouteProgress = () => {
  const location = useLocation();

  useEffect(() => {
    NProgress.start();
    NProgress.done();
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <>
      <RouteProgress />
      <Suspense
        fallback={
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              fontFamily: 'inherit',
            }}
          >
            در حال بارگذاری...
          </div>
        }
      >
        <Routes>
          <Route path="/test" element={<Test />} />
          <Route path="/login" element={<Login />} />
          <Route path="/chunk" element={<Chunk />} />

          <Route element={<PrivateRoute />}>
            <Route path="/" element={<Main />} />
            <Route path="/main" element={<Main />} />

            <Route path="/product/list" element={<ProductList />} />
            <Route path="/product/create" element={<ProductCreate />} />
            <Route path="/product/:id/update" element={<ProductUpdate />} />
            <Route
              path="/product/:id/photos/create"
              element={<ProductPhotoCreate />}
            />
            <Route
              path="/product/:id/item/list"
              element={<ProductItemList />}
            />
            <Route
              path="/product/:id/item/create"
              element={<ProductItemCreate />}
            />
            <Route
              path="/product/:product_id/item/:product_item_id/update"
              element={<ProductItemUpdate />}
            />
            <Route path="/product/:id/label" element={<ProductLabel />} />

            <Route path="/category/list" element={<CategoryList />} />
            <Route path="/category/create" element={<CategoryCreate />} />
            <Route path="/category/:id/update" element={<CategoryUpdate />} />
            <Route
              path="/category/:id/photos/create"
              element={<CategoryPhotoCreate />}
            />
            <Route path="/category/:id/sub" element={<SubCategoryList />} />
            <Route
              path="/category/:id/sub/create"
              element={<SubCategoryCreate />}
            />

            <Route path="/company/list" element={<CompanyList />} />
            <Route path="/company/create" element={<CompanyCreate />} />
            <Route path="/company/:id/update" element={<CompanyUpdate />} />
            <Route
              path="/company/:id/photos/create"
              element={<CompanyPhotoCreate />}
            />
            <Route
              path="/company/:id/photos/list"
              element={<CompanyPhotoList />}
            />

            <Route path="/banner/list" element={<BannerList />} />
            <Route path="/banner/create" element={<BannerCreate />} />
            <Route path="/banner/:id/update" element={<BannerUpdate />} />

            <Route path="/user/list" element={<UserList />} />
            <Route path="/user/create" element={<UserCreate />} />
            <Route path="/user/:id/update" element={<UserUpdate />} />
            <Route path="/user/:id/cart/list" element={<CartList />} />
            <Route
              path="/user/:id/cart/:reference/item"
              element={<CartItem />}
            />

            <Route path="/task/list" element={<TaskList />} />
            <Route path="/task/create" element={<TaskCreate />} />
            <Route path="/task/:uri_task_id/view" element={<TaskView />} />
            <Route path="/task/:uri_task_id/update" element={<TaskUpdate />} />
            <Route path="/archive/:uri_date" element={<TaskArchive />} />

            <Route path="/role/list" element={<RoleList />} />
            <Route path="/role/create" element={<RoleCreate />} />
            <Route path="/role/:id/update" element={<RoleUpdate />} />
            <Route path="/role/:id/permissions" element={<RolePermission />} />

            <Route path="/salary" element={<SalaryCalculation />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
